import { toast } from '@affine/component';
import {
  databasePermanentlyDeleteMutation,
  databaseRestoreMutation,
  databaseTrashMutation,
} from '@affine/graphql';
import { I18n } from '@affine/i18n';
import type { DatabaseAppUsageRead } from '@blocksuite/affine/blocks/database';
import { OnEvent, Service } from '@toeverything/infra';
import { nanoid } from 'nanoid';

import type { WorkspaceServerService } from '../../cloud';
import {
  classifyLifecycleMutationError,
  classifyOfflineMutationError,
  OFFLINE_DELETE_COPY_CONFIRMATION,
  PERMANENT_DELETE_DOMAIN_CODES,
} from '../../offline-mutation-outbox/services/offline-mutation-error';
import {
  type OfflineMutationOutboxService,
  OfflineMutationTransientError,
} from '../../offline-mutation-outbox/services/offline-mutation-outbox';
import {
  OFFLINE_DELETION_HOLD_MS,
  type OfflineMutationRecord,
} from '../../offline-mutation-outbox/types';
import type { Workspace, WorkspaceService } from '../../workspace';
import { WorkspaceInitialized } from '../../workspace/events';
import { hasAppBaseErrorCode } from '../hooks/app-base-error-code';
import type {
  BlockDeletionPresentationService,
  BlockLifecycleRecord,
  BlockLocalVisibility,
} from './block-deletion-presentation';
import type { DatabaseDiscoveryService } from './database-discovery';

/**
 * A table of a cloud workspace goes to the trash and comes back through the
 * offline mutation outbox: queued at once, sent when online, and confirmed by
 * the server's receipt. The client never changes the host doc before the
 * receipt; the committed host syncs back. Until then the queued trash shows
 * through BlockDeletionPresentationService, like that of an automation.
 */

export type DatabaseDeletionTarget = {
  docId: string;
  blockId: string;
  databaseId: string;
};
type DatabaseLifecycleOperation =
  | 'database.trash'
  | 'database.restore'
  | 'database.permanentDelete';
type DatabaseLifecycleRecord = Extract<
  BlockLifecycleRecord,
  { operation: DatabaseLifecycleOperation }
>;

const DATABASE_DELETE_BLOCKED = 'DATABASE_DELETE_BLOCKED';
/**
 * Codes of a refusal: the server will not do it, so the record stops. Every
 * other error (settling, a busy table or workspace, an internal error, no
 * code, the network) retries under the same operation id.
 */
const REFUSAL_CODES = [
  DATABASE_DELETE_BLOCKED,
  'OPERATION_TARGET_MISMATCH',
  'FORBIDDEN',
  'BAD_REQUEST',
  'NOT_FOUND',
] as const;

/** What the user reads when the server refuses, by failure class. */
const TRASH_REFUSAL_MESSAGE: Readonly<Record<string, string>> = {
  database_trash_protected: 'com.streamlain.trash.database-trash-protected',
  database_trash_location_changed:
    'com.streamlain.trash.database-trash-location-changed',
  database_trash_host_trashed:
    'com.streamlain.trash.database-trash-host-trashed',
};
const RESTORE_REFUSAL_MESSAGE: Readonly<Record<string, string>> = {
  database_restore_occupied: 'com.streamlain.trash.database-restore-occupied',
  database_restore_unavailable:
    'com.streamlain.trash.database-restore-unavailable',
};
const TRASH_REFUSED = 'com.streamlain.trash.database-trash-failed';
const RESTORE_REFUSED = 'com.streamlain.trash.restore-failed';
/**
 * A table no app reads may disappear while its trash is queued; one an app
 * reads, or whose usage is unknown, stays visible until the receipt.
 */
export function databaseLocalVisibility(
  usage: DatabaseAppUsageRead
): BlockLocalVisibility {
  if (usage.status !== 'ready') return 'keep';
  return usage.usages.length === 0 ? 'hide' : 'keep';
}

@OnEvent(
  WorkspaceInitialized,
  (service: DatabaseDeletionService) => service.onWorkspaceInitialized
)
export class DatabaseDeletionService extends Service {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly outbox: OfflineMutationOutboxService,
    private readonly discovery: DatabaseDiscoveryService,
    private readonly presentation: BlockDeletionPresentationService
  ) {
    super();
    this.disposables.push(
      this.outbox.registerExecutor('database.permanentDelete', mutation =>
        this.send(mutation)
      ),
      this.outbox.registerExecutor('database.trash', mutation =>
        this.send(mutation)
      ),
      this.outbox.registerExecutor('database.restore', mutation =>
        this.send(mutation)
      )
    );
    const failedSubscription = this.outbox.failed$.subscribe(mutation => {
      if (isDatabaseLifecycleMutation(mutation)) this.onFailed(mutation);
    });
    this.disposables.push(() => failedSubscription.unsubscribe());
  }

  onWorkspaceInitialized(_workspace: Workspace): void {
    // The event decorator constructs this service when the workspace opens,
    // so a table lifecycle queued before a reload is sent again.
  }

  /**
   * Queues the trash of a table. A second request while one is queued
   * returns the queued one; a new cycle after a restore gets a new operation.
   * A restore of the table not yet sent is withdrawn instead, and nothing is
   * sent.
   *
   * @returns the queued request, or null when a queued restore was withdrawn
   */
  async requestTrash(
    target: DatabaseDeletionTarget,
    visibility: BlockLocalVisibility
  ): Promise<{ mutationId: string } | null> {
    if (await this.withdrawUnsent('database.restore', target)) return null;
    const mutation = await this.outbox.enqueue({
      operation: 'database.trash',
      payload: lifecyclePayload(target, visibility),
      idempotencyKey: `database.trash:${target.docId}:${target.databaseId}`,
      dedupePending: true,
      holdMs: OFFLINE_DELETION_HOLD_MS,
      orderKey: databaseOrderKey(target),
    });
    this.presentation.announceQueued(mutation.id);
    return { mutationId: mutation.id };
  }

  /**
   * Queues the restore of a trashed table; it stays visible meanwhile. A
   * trash of the table not yet sent is withdrawn instead, and nothing is
   * sent; one already sent is restored after it.
   *
   * @returns the queued request, or null when a queued trash was withdrawn
   */
  async requestRestore(
    target: DatabaseDeletionTarget
  ): Promise<{ mutationId: string } | null> {
    if (await this.withdrawUnsent('database.trash', target)) return null;
    const mutation = await this.outbox.enqueue({
      operation: 'database.restore',
      payload: lifecyclePayload(target, 'keep'),
      idempotencyKey: `database.restore:${target.docId}:${target.databaseId}`,
      dedupePending: true,
      orderKey: databaseOrderKey(target),
    });
    return { mutationId: mutation.id };
  }

  async requestPermanentDelete(
    target: Pick<DatabaseDeletionTarget, 'docId' | 'blockId'> &
      Partial<Pick<DatabaseDeletionTarget, 'databaseId'>>
  ): Promise<{ mutationId: string }> {
    const mutation = await this.outbox.enqueue({
      operation: 'database.permanentDelete',
      payload: { docId: target.docId, blockId: target.blockId },
      idempotencyKey: `database.permanentDelete:${target.docId}:${target.blockId}`,
      dedupePending: true,
      holdMs: OFFLINE_DELETION_HOLD_MS,
      orderKey: target.databaseId
        ? `database:${target.databaseId}`
        : `database:${target.docId}:${target.blockId}`,
    });
    return { mutationId: mutation.id };
  }

  /**
   * Withdraws the queued counter operation of the same table through the
   * outbox's atomic cancel; true when one will never be sent.
   */
  private async withdrawUnsent(
    operation: DatabaseLifecycleOperation,
    target: DatabaseDeletionTarget
  ): Promise<boolean> {
    let withdrawn = false;
    for (const mutation of await this.outbox.refreshPendingMutations()) {
      if (
        mutation.operation === operation &&
        isDatabaseLifecycleMutation(mutation) &&
        mutation.operation !== 'database.permanentDelete' &&
        mutation.payload.databaseId === target.databaseId &&
        (await this.outbox.cancelPending(mutation.id))
      ) {
        withdrawn = true;
      }
    }
    return withdrawn;
  }

  private async send(mutation: DatabaseLifecycleRecord): Promise<void> {
    const gql = this.workspaceServerService.server?.gql;
    if (!gql) {
      throw new OfflineMutationTransientError(
        'No server for the table lifecycle'
      );
    }
    const { docId } = mutation.payload;
    try {
      await this.workspaceService.workspace.engine.doc.flushAndWaitForSynced(
        docId
      );
      if (mutation.operation === 'database.permanentDelete') {
        await gql({
          query: databasePermanentlyDeleteMutation,
          variables: {
            input: {
              workspaceId: mutation.workspaceId,
              docId,
              blockId: mutation.payload.blockId,
            },
          },
        });
      } else {
        const { databaseId, operationId } = mutation.payload;
        const input = {
          workspaceId: mutation.workspaceId,
          databaseId,
          operationId,
        };
        if (mutation.operation === 'database.trash') {
          await gql({ query: databaseTrashMutation, variables: { input } });
        } else {
          await gql({ query: databaseRestoreMutation, variables: { input } });
        }
      }
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === OFFLINE_DELETE_COPY_CONFIRMATION
      )
        throw error;
      if (mutation.operation === 'database.permanentDelete') {
        const classified = classifyOfflineMutationError(
          error,
          PERMANENT_DELETE_DOMAIN_CODES
        );
        classified.message = hasAppBaseErrorCode(
          error,
          'DATABASE_DELETE_TOO_MANY_APPS'
        )
          ? I18n.t('com.streamlain.trashOperation.delete.tooManyApps')
          : hasAppBaseErrorCode(error, 'DATABASE_TABLE_BUSY')
            ? I18n.t('com.streamlain.streamlain.database-delete.table-busy')
            : I18n.t(
                'com.streamlain.streamlain.database-delete.server-failed',
                {
                  error: classified.message,
                }
              );
        throw classified;
      }
      throw classifyLifecycleMutationError(
        error,
        DATABASE_DELETE_BLOCKED,
        REFUSAL_CODES
      );
    }
    this.discovery.refresh();
  }

  /** A refusal shows once and leaves; an unconfirmed request stays. */
  private onFailed(mutation: DatabaseLifecycleRecord): void {
    if (
      mutation.failure !== 'refused' ||
      mutation.lastError === OFFLINE_DELETE_COPY_CONFIRMATION
    )
      return;
    const messages =
      mutation.operation === 'database.trash'
        ? TRASH_REFUSAL_MESSAGE
        : RESTORE_REFUSAL_MESSAGE;
    const fallback =
      mutation.operation === 'database.trash' ? TRASH_REFUSED : RESTORE_REFUSED;
    toast(
      mutation.operation === 'database.permanentDelete'
        ? (mutation.lastError ?? I18n.t(TRASH_REFUSED))
        : I18n.t(messages[mutation.lastError ?? ''] ?? fallback)
    );
    this.outbox.removeFailed(mutation.id).catch(error => {
      console.error('[DatabaseDeletion] Removing a refusal failed:', error);
    });
  }
}

function lifecyclePayload(
  { docId, blockId, databaseId }: DatabaseDeletionTarget,
  localVisibility: BlockLocalVisibility
) {
  return { docId, blockId, databaseId, operationId: nanoid(), localVisibility };
}

/** Trash and restore of one table leave in the order they were queued. */
function databaseOrderKey({ databaseId }: DatabaseDeletionTarget): string {
  return `database:${databaseId}`;
}

function isDatabaseLifecycleMutation(
  mutation: OfflineMutationRecord
): mutation is DatabaseLifecycleRecord {
  return (
    mutation.operation === 'database.trash' ||
    mutation.operation === 'database.restore' ||
    mutation.operation === 'database.permanentDelete'
  );
}
